export interface Identifiable {
  id: string
  [key: string]: unknown
}

/**
 * Merges or replaces properties of two objects based on specified keys.
 * If the objects have different IDs, the new object is returned.
 * Otherwise, it merges the two objects, with deep merging for specified keys.
 *
 * @template T - The type of the objects being merged.
 * @param oldObj - The original object.
 * @param newObj - The new object.
 * @param deepMergeKeys - The keys to be merged.
 * @returns The merged or replaced object.
 */
export function mergeOrReplaceObjects<T extends Identifiable>(
  oldObj: T,
  newObj: T,
  deepMergeKeys: (keyof T)[]
): T {
  if (oldObj.id && newObj.id && oldObj.id !== newObj.id) {
    return newObj
  } else {
    const mergedObjs: Partial<T> = {}
    for (const key of deepMergeKeys) {
      if (newObj[key] && typeof oldObj[key] === 'object' && typeof newObj[key] === 'object') {
        mergedObjs[key] = { ...(oldObj[key] as object), ...(newObj[key] as object) } as T[keyof T]
      }
    }
    return Object.assign({}, oldObj, newObj, mergedObjs)
  }
}

/**
 * Deeply compares two objects or arrays for equality.
 *
 * @param obj1 - First object/value
 * @param obj2 - Second object/value
 * @returns True if they are deeply equal
 */
export function isDeepEqual(obj1: any, obj2: any): boolean {
  if (obj1 === obj2) return true

  if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
    return false
  }

  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    return false
  }

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false
    for (let i = 0; i < obj1.length; i++) {
      if (!isDeepEqual(obj1[i], obj2[i])) return false
    }
    return true
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) return false

  for (const key of keys1) {
    if (!Object.prototype.hasOwnProperty.call(obj2, key) || !isDeepEqual(obj1[key], obj2[key])) {
      return false
    }
  }

  return true
}
